/* eslint-disable max-lines */
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { color } from 'styles/colors'
import { Button, ButtonTheme } from '@nexiahome/ui-common/components/primitives/buttons'
import Spinner from 'components/Spinner'

import { ButtonContainer, PageTitle, SinglePanePage, screenSize } from 'styles/shared'
import Brand from 'helpers/brand'
import usePaywallActiveAcct from 'hooks/usePaywallActiveAcct'
import usePaywallPremiumAcct from 'hooks/usePaywallPremiumAcct'
import usePaywallPlans from 'hooks/usePaywallPlans'
import { BillingData } from 'types/BillingData'
import ChangePlanModal from 'pages/paywall/ChangePlanModal'
import CancelModal from 'pages/paywall/CancelModal'
import { ComponentStyleSheet } from '@nexiahome/ui-common/themes'
import LabelValuePair from 'pages/paywall/LabelValuePair'
import { shortDateTime, shortDate } from 'helpers/formatters'
import GA4 from 'helpers/ga4'

const PaywallDetails = () => {
  const { dealerId } = useParams()
  const navigate = useNavigate()

  const { account, isPaywallAcctLoaded, planChange, isPaywallPlanChangeLoaded } = usePaywallActiveAcct(dealerId)
  const { premiumAccount, isPremiumAcctLoaded } = usePaywallPremiumAcct(dealerId)
  const { plans, yearlyPlanNo, monthlyPlanNo, arePaywallPlansLoaded } = usePaywallPlans(dealerId)

  const [displayChangePlanModal, setDisplayChangePlanModal] = useState(false)
  const [displayCancelModal, setDisplayCancelModal] = useState(false)
  const [paywallAcct, setPaywallAcct] = useState<BillingData>()
  const [planType, setPlanType] = useState<string>()
  const [currentPlan, setCurrentPlan] = useState(yearlyPlanNo)
  const [planIsDeactivated, setPlanIsDeactivated] = useState(false)
  const [planIsTrial, setPlanIsTrial] = useState(false)
  const [effectiveDate, setEffectiveDate] = useState<string>()

  useEffect(() => {
    if (isPaywallAcctLoaded && arePaywallPlansLoaded && isPaywallPlanChangeLoaded && isPaywallPlanChangeLoaded && isPremiumAcctLoaded) {
      if (account) {
        setPaywallAcct(account.billingContact)

        if (account.planNo) {
          let planType = 'diag_yearly'
          for (let i = 0; i < plans.length; i++) {
            if (plans[i].no.toString() === account.planNo) {
              planType = plans[i].planId
              break
            }
          }

          if (planType === 'diag_yearly') {
            setPlanType('Yearly')
            setCurrentPlan(yearlyPlanNo)
            setEffectiveDate(premiumAccount?.effectiveDate)
          } else if (planType === 'diag_monthly') {
            setPlanType('Monthly')
            setCurrentPlan(monthlyPlanNo)
            setEffectiveDate(premiumAccount?.effectiveDate)
          } else if (planType === 'diag_trial') {
            setPlanType('Trial')
            setCurrentPlan(0)
            setPlanIsTrial(true)
            setEffectiveDate(premiumAccount?.effectiveDate)
          }
        }
      } else {
        setPlanType('Basic')
        setPlanIsDeactivated(true)
      }
    }
  }, [isPaywallAcctLoaded, account, arePaywallPlansLoaded, isPaywallPlanChangeLoaded, isPremiumAcctLoaded, plans, yearlyPlanNo, monthlyPlanNo, premiumAccount])

  const onCloseModalCallback = (reload: boolean) => {
    setDisplayChangePlanModal(false)
    setDisplayCancelModal(false)
    if (reload) {
      navigate(-1)
    }
  }

  const sendGA4Event = (label: string) => {
    GA4.sendEvent({
      action: 'Click Premium Account Settings Option',
      category: 'Premium Settings',
      label: `Button: ${label}`
    })
  }

  const onChangePlanTypeClick = () => {
    sendGA4Event('Change Plan')
    setDisplayChangePlanModal(!displayChangePlanModal)
  }

  const onCancelPlanRenewalClick = () => {
    sendGA4Event('Cancel Plan Renewal')
    setDisplayCancelModal(!displayCancelModal)
  }

  const onChangeCreditCardInfoClick = () => {
    sendGA4Event('Change Credit Card Information')
    navigate(`/paywall/account/${dealerId}/${currentPlan}/${planChange ? planChange.targetPlanId : planType === 'Yearly' ? 'diag_yearly' : 'diag_monthly'}`)
  }

  const onStatementsClick = () => {
    sendGA4Event('Statements')
    navigate(`/paywall/statements/${dealerId}`)
  }

  const planTypeDisplay = () => {
    let value = planType || 'Basic'

    if (planChange) {
      if (planChange.targetPlanId === 'diag_yearly') {
        value = `${planType} (Change to Yearly will occur on ` + shortDate(planChange.effectiveDate) + ')'
      } else if (planChange.targetPlanId === 'diag_monthly') {
        value = `${planType} (Change to Monthly will occur on ` + shortDate(planChange.effectiveDate) + ')'
      } else if (planChange.targetPlanId === null) {
        value = `${planType} (cancellation will take effect on ` + shortDate(planChange.effectiveDate) + ')'
      }
    }

    return value
  }

  if (!isPaywallAcctLoaded || !isPaywallPlanChangeLoaded || !isPremiumAcctLoaded || !arePaywallPlansLoaded) {
    return <Spinner variant="page" />
  }

  return (
    <>
      <S.Top>
        <S.ProductLogo src={Brand.premiumProductLogoPath()} alt={`${Brand.brandName()} logo`} />
        <div />
      </S.Top>
      <S.SinglePanePage>
        <S.PageTitle>Account Settings</S.PageTitle>
        <S.Form>
          <S.InnerContainer>
            <S.Explanation>Account Summary</S.Explanation>
            <S.LabelValuePair key={paywallAcct?.email} label={'Email'} value={paywallAcct?.email} />
            <S.LabelValuePair label={'Plan type'} value={planTypeDisplay()} />
            {!planIsTrial &&
              <>
                {planIsDeactivated &&
                  <>
                    <Button decoration="link" text="Reactivate Account" styles={secondaryButtonStyle} onPress={() => navigate(`/paywall/options/${dealerId}`)} />
                    <S.InactiveLabel>Inactive since {shortDate(premiumAccount?.updatedAt)}</S.InactiveLabel>
                  </>
                }
                {!planIsDeactivated &&
                  <>
                    <Button decoration="link" text="Change Plan Type" styles={secondaryButtonStyle} onPress={onChangePlanTypeClick} />
                    <Button decoration="link" text="Cancel Plan Renewal" styles={secondaryButtonStyle} onPress={onCancelPlanRenewalClick} />
                  </>
                }
              </>
            }
          </S.InnerContainer>
          <S.Line />
          {!planIsDeactivated && !planIsTrial &&
            <>
              <S.InnerContainer>
                <S.Explanation>Billing Information</S.Explanation>
                <S.LabelValuePair label={'Address'} value={paywallAcct?.address} />
                {paywallAcct?.city && paywallAcct?.state && paywallAcct?.zip &&
                  <S.AddressSecondLine>
                    {[paywallAcct?.city, paywallAcct?.state, paywallAcct?.zip].join(', ')}
                  </S.AddressSecondLine>
                }
                <Button
                  decoration="link"
                  text="Change credit card information"
                  styles={secondaryButtonStyle}
                  onPress={onChangeCreditCardInfoClick}
                />
              </S.InnerContainer>
              <S.Line />
            </>
          }
          {!planIsTrial &&
            <S.InnerContainer>
              <Button decoration="link" text="Statements" styles={secondaryButtonStyle} onPress={onStatementsClick} />
            </S.InnerContainer>
          }
          <S.LastUpdated>Last updated by {premiumAccount?.updatedBy} on {shortDateTime(premiumAccount?.updatedAt)}</S.LastUpdated>
        </S.Form>
        <S.ButtonContainer>
          <Button decoration="positive" text="Close" onPress={() => navigate(-1)} />
        </S.ButtonContainer>
      </S.SinglePanePage>
      {displayChangePlanModal &&
        <ChangePlanModal
          closeCallback={onCloseModalCallback}
          dealerId={Number(dealerId)}
          planChange={planChange}
          currentPlan={currentPlan}
          yearlyPlanNo={yearlyPlanNo}
        />}
      {displayCancelModal &&
        <CancelModal
          closeCallback={onCloseModalCallback}
          dealerId={Number(dealerId)}
          planChange={planChange}
          effectiveDate={effectiveDate}
        />}
    </>
  )
}

const secondaryButtonStyle: ComponentStyleSheet<ButtonTheme> = {
  button: {
    paddingLeft: 0,
    alignSelf: 'flex-start',
    marginTop: 10
  },
  text: {
    fontSize: 16,
    textTransform: 'none'
  }
}

export default PaywallDetails

const S = {
  AddressSecondLine: styled.div`
    font-size: 16px;
    color: ${color('premiumText')};
  `,
  ButtonContainer: styled(ButtonContainer)`
    border-top: none;
    margin-top: 0px;
  `,
  Data: styled.div`
    display: flex;
  `,
  InactiveLabel: styled.div`
    font-size: 12px;
    width: 100%;
    color: ${color('premiumText')};
  `,
  LabelValuePair: styled(LabelValuePair)`
    margin-top: 15px;
  `,
  LastUpdated: styled.i`
    margin-top: 20px;
    text-align: center;
    font-size: 12px;
    width: 100%;
    color: ${color('premiumText')};
  `,
  Line: styled.div`
    border: 1.5px solid ${color('border')};
    width: 100%;
    margin-top: 25px;
    margin-bottom: 25px;
  `,
  Explanation: styled.h2`
    display: flex;
    margin-bottom: 10px;
    text-align: left;
    text-transform: none;
    font-size: 24px;
  `,
  Form: styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    border-radius: 10px;
    background-color: #F3F7F8;
    padding: 15px;
    max-width: fit-content;
  `,
  InnerContainer: styled.div`
    display: flex;
    flex-direction: column;
    width: 80%;
  `,
  Link: styled.a`
    padding-left: 5px
  `,
  PageTitle: styled(PageTitle)`
    margin-top: 10px;
    text-transform: none;
  `,
  ProductLogo: styled.img`
    height: 50px;
    @media ${screenSize.small} {
      max-width: 290px;
    }
  `,
  SinglePanePage: styled(SinglePanePage)`
    width: fit-content;
    @media ${screenSize.small} {
      padding: 20px;
    }
  `,
  Top: styled.div`
    display: flex;
    padding-top: 10px;
    padding-left: 20px;
  `
}
